import videojs from 'video.js'
import { registerPluginOnce } from '../utils.js'

const Plugin = videojs.getPlugin('plugin')

const MIN_CHAPTER_DIFF = 2 // min width of chapter section in %

// Default options for the plugin.
const defaults = {
    start: null, // in seconds
    end: null, // in seconds
    name: null
}

/**
 * An advanced Video.js plugin. For more information on the API
 *
 * See: https://blog.videojs.com/feature-spotlight-advanced-plugins/
 */
class NFBChapter extends Plugin {
  /**
   * Create a NFBNextEpisode plugin instance.
   *
   * @param  {Player} player
   *         A Video.js Player instance.
   *
   * @param  {Object} [options]
   *         An optional options object.
   *
   *         While not a core part of the Video.js plugin architecture, a
   *         second argument of options is a convenient way to accept inputs
   *         from your plugin's caller.
   */
  constructor (player, options) {
    // the parent class will add player under this.player
    super(player)
    this.log.level(videojs.log.level())
    this.options = videojs.obj.merge(defaults, options)

    const playerState = this.player.readyState()
    if (!playerState || playerState < 2) {
      this.player.one('loadeddata', this.init)
    } else {
      this.init()
    }
  }

  init = () => {
    this.progressControlContainer = this.player.el().querySelector('.vjs-progress-control-container')
    this.progressControl = this.player.el().querySelector('.vjs-progress-control')

    const initCallback = () => {
      this.restartChapter()
      this.createChapterLayout()
    }

    if (this.progressControlContainer?.offsetParent) {
      // Trigger now if progress control is visible in DOM
      initCallback()
    } else {
      // If not (ex: overlay), init on play
      this.player.one('play', initCallback)
    }
  }

  restartChapter = () => {
    this.player.off('timeupdate', this.progressHasChanged)
    this.player.on('timeupdate', this.progressHasChanged)
    this.player.currentTime(this.options.start)
    this.player.play()
  }

  createChapterLayout = () => {
    if (this.progressControlContainer && this.progressControl) {
      // Create marker
      const marker = document.createElement('button')
      marker.classList.add('vjs-chapter-marker', 'vjs-control')
      marker.tabIndex = 0
      marker.ariaLable = this.player.localize('Restart chapter')
      const icon = document.createElement('i')
      icon.classList.add('icon-marker', 'nfb-icon')
      marker.appendChild(icon)
      this.progressControlContainer.appendChild(marker)

      // Create separators
      const totalTime = this.player.duration()
      const startPositionPct = (this.options.start / totalTime) * 100
      const endPositionPct = Math.max(startPositionPct + MIN_CHAPTER_DIFF, (this.options.end / totalTime) * 100)
      const startSeparator = document.createElement('div')
      const endSeparator = document.createElement('div')
      startSeparator.classList.add('vjs-chapter-separator')
      endSeparator.classList.add('vjs-chapter-separator')

      // Create white progress overlay
      const progressOverlay = document.createElement('div')
      progressOverlay.classList.add('vjs-chapter-progress-overlay')
    
      this.progressControl.appendChild(progressOverlay)
      this.progressControl.appendChild(startSeparator)
      this.progressControl.appendChild(endSeparator)

      // Position overlay and separators
      const separatorWidth = startSeparator.getBoundingClientRect().width
      progressOverlay.style.width = `calc(${endPositionPct - startPositionPct}% + ${separatorWidth/2}px)`
      progressOverlay.style.left = `${startPositionPct}%`
      startSeparator.style.left = `calc(${startPositionPct}% - ${separatorWidth/2}px)`
      endSeparator.style.left = `calc(${endPositionPct}% - ${separatorWidth/2}px)`

      // Position marker
      const isMinWidth = Math.round(endPositionPct - startPositionPct) <= MIN_CHAPTER_DIFF
      const markerPosition = isMinWidth ? startPositionPct + ((endPositionPct - startPositionPct) / 2) : startPositionPct
      marker.style.left = isMinWidth 
        ? `calc(${markerPosition}% - ${(marker.getBoundingClientRect().width / 2)}px)`
        : `calc(${markerPosition}% - ${separatorWidth}px`
      marker.addEventListener('click', this.chapterMarkerClicked)

      // Chapter name
      if (this.options.name) {
        const timeContainer = this.player.el().querySelector('.vjs-time-container')
        if (timeContainer) {
          const chapterNameElement = document.createElement('div')
          chapterNameElement.classList.add('vjs-chapter-name')
          chapterNameElement.innerHTML = this.options.name
          timeContainer.appendChild(chapterNameElement)
        }
      }
    }
  }

  chapterMarkerClicked = () => {
    this.restartChapter()
  }

  progressHasChanged = () => {
    const currentTime = this.player.currentTime()
    if (Math.round(currentTime) === Math.round(this.options.end)) {
      this.player.pause()
      this.player.off('timeupdate', this.progressHasChanged)
    } else if (currentTime > this.options.end || currentTime < this.options.start) {
      this.player.off('timeupdate', this.progressHasChanged)
    }
  }
}

// Define default values for the plugin's `state` object here.
NFBChapter.defaultState = {}

// Include the version number.
NFBChapter.VERSION = '1.0.0'

// Register the plugin
registerPluginOnce(videojs, 'nfbChapter', NFBChapter)

export default NFBChapter
